import { useCallback, useState } from 'react';
import * as tasksSvc from '@/services/tasks.service';
import type { Task } from '@/services/tasks.service';
import { useToast } from '@/hooks/useToast';

export type RescheduleUnit = 'week' | 'month';

/**
 * Shared "reschedule this task" action used by every Done surface (the Done split
 * button, CompleteModal, the drilldown confirm modal, …). Pushes the task's due
 * date to today + 1 week / 1 month via tasksSvc.rescheduleTaskFromToday, shows a
 * toast (row surfaces have no inline error area), and exposes a per-unit loading
 * flag. `onDone` receives the updated Task so callers holding local row state can
 * patch `lastDue` optimistically; query-based callers ignore it and re-fetch.
 */
export function useTaskReschedule() {
  const toast = useToast();
  const [rescheduling, setRescheduling] = useState<RescheduleUnit | null>(null);

  const reschedule = useCallback(
    async (taskId: number, unit: RescheduleUnit, onDone?: (updated: Task) => void) => {
      setRescheduling(unit);
      try {
        const updated = await tasksSvc.rescheduleTaskFromToday(taskId, unit);
        toast.success('Task rescheduled', `Now due 1 ${unit} from today.`);
        onDone?.(updated);
      } catch (err) {
        toast.error('Reschedule failed', err instanceof Error ? err.message : undefined);
      } finally {
        setRescheduling(null);
      }
    },
    [toast],
  );

  return { reschedule, rescheduling };
}
